'use client'

import { api } from '@/lib/api'
import { parseCreateSessionResponse } from '@/lib/compiler/guards'
import type { CreateSessionResponse } from '@/lib/compiler/types'

export const AUTH_PROJECT_SESSION_PREFIX = 'rudrise-auth-project-session-'

export interface AuthProjectSessionRecord {
  projectId: string
  sessionId: string
  status: string
}

// Stores projectId + sessionId + status together as one JSON record per project key,
// instead of a bare session id string, so the localStorage entry is self-describing.
export function saveAuthProjectSessionId(projectId: string, sessionId: string, status = 'active'): void {
  if (typeof window === 'undefined') return
  try {
    const record: AuthProjectSessionRecord = { projectId, sessionId, status }
    localStorage.setItem(`${AUTH_PROJECT_SESSION_PREFIX}${projectId}`, JSON.stringify(record))
  } catch {
    // ignore quota errors
  }
}

export function loadAuthProjectSession(projectId: string): AuthProjectSessionRecord | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = localStorage.getItem(`${AUTH_PROJECT_SESSION_PREFIX}${projectId}`)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<AuthProjectSessionRecord>
    if (typeof parsed.sessionId !== 'string') return null
    return { projectId, sessionId: parsed.sessionId, status: parsed.status ?? 'active' }
  } catch {
    // Pre-migration entries stored a bare session id string, not JSON — treat as absent
    // so a fresh session gets created rather than crashing on the old format.
    return null
  }
}

export function loadAuthProjectSessionId(projectId: string): string | null {
  return loadAuthProjectSession(projectId)?.sessionId ?? null
}

export function clearAuthProjectSessionId(projectId: string): void {
  if (typeof window === 'undefined') return
  try {
    localStorage.removeItem(`${AUTH_PROJECT_SESSION_PREFIX}${projectId}`)
  } catch {
    // ignore
  }
}

const URL_SESSION_PARAM_KEYS = ['compilerSessionId', 'sessionId'] as const

/** Read a shareable compiler session id from the editor URL (cross-browser collaboration). */
export function getCollaborationSessionIdFromUrl(): string | null {
  if (typeof window === 'undefined') return null
  try {
    const params = new URLSearchParams(window.location.search)
    for (const key of URL_SESSION_PARAM_KEYS) {
      const value = params.get(key)?.trim()
      if (value) return value
    }
  } catch {
    // ignore
  }
  return null
}

/** Keep the active compiler session id in the URL so other browsers can join the same CRDT room. */
export function syncCollaborationSessionIdToUrl(sessionId: string): void {
  if (typeof window === 'undefined' || !sessionId) return
  try {
    const url = new URL(window.location.href)
    if (url.searchParams.get('compilerSessionId') === sessionId) {
      console.log('[Collaboration] URL already has this session — share this exact link', {
        sessionId,
        url: url.toString(),
      })
      return
    }
    url.searchParams.set('compilerSessionId', sessionId)
    // Drop legacy alias so there's one source of truth.
    url.searchParams.delete('sessionId')
    window.history.replaceState({}, '', url.toString())
    console.log(
      '[Collaboration] session id synced to URL — copy THIS full link into the other browser (projectId alone is not enough)',
      { sessionId, url: url.toString() },
    )
  } catch {
    // ignore
  }
}

/** Compare two collaboration URLs / ids for debugging split-brain rooms. */
export function logCollaborationRoomMismatch(localSessionId: string, peerHint?: string): void {
  console.error(
    '[Collaboration] SPLIT ROOM — cursors/edits will NOT sync until both browsers share the same compilerSessionId',
    {
      thisTabSessionId: localSessionId,
      peerHint: peerHint ?? null,
      thisTabUrl: typeof window !== 'undefined' ? window.location.href : null,
      fix: 'Copy the full URL (including ?compilerSessionId=...) from one browser and open it in the other. Opening the project from the dashboard in each browser creates SEPARATE sessions.',
    },
  )
}

const COMPILE_LEADER_TTL_MS = 45_000
const TAB_ID_KEY = 'rudrise-tab-id'

function getOrCreateTabId(): string {
  if (typeof window === 'undefined') return 'server'
  try {
    let id = sessionStorage.getItem(TAB_ID_KEY)
    if (!id) {
      id = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
      sessionStorage.setItem(TAB_ID_KEY, id)
    }
    return id
  } catch {
    return `ephemeral-${Date.now()}`
  }
}

/**
 * Only one browser tab per compiler session should auto-compile on connect.
 * Prevents dual compile_trigger from Chrome+Safari racing and cross-tab PDF errors.
 */
export function tryClaimAutoCompileLeader(sessionId: string): boolean {
  if (typeof window === 'undefined' || !sessionId) return true
  const key = `rudrise-compile-leader-${sessionId}`
  const tabId = getOrCreateTabId()
  const now = Date.now()
  try {
    const raw = sessionStorage.getItem(key)
    if (raw) {
      const parsed = JSON.parse(raw) as { tabId?: string; ts?: number }
      if (
        parsed.tabId &&
        parsed.tabId !== tabId &&
        typeof parsed.ts === 'number' &&
        now - parsed.ts < COMPILE_LEADER_TTL_MS
      ) {
        return false
      }
    }
    sessionStorage.setItem(key, JSON.stringify({ tabId, ts: now }))
    return true
  } catch {
    return true
  }
}

function normalizeCompilerSessionPayload(data: unknown): CreateSessionResponse | null {
  if (!data || typeof data !== 'object') return null

  const record = data as Record<string, unknown>
  const resolvedId =
    typeof record.session_id === 'string'
      ? record.session_id
      : typeof record.id === 'string'
        ? record.id
        : ''
  const sessionStatus = typeof record.status === 'string' ? record.status : 'active'
  if (!resolvedId || sessionStatus !== 'active') return null

  const parsed = parseCreateSessionResponse({
    session_id: resolvedId,
    created_at:
      (typeof record.created_at === 'string' && record.created_at) ||
      (typeof record.createdAt === 'string' && record.createdAt) ||
      new Date().toISOString(),
    auto_compile: record.auto_compile ?? record.autoCompile ?? true,
    ws_url: record.ws_url,
    crdt: record.crdt,
    message: record.message,
    pdf_url: record.pdf_url,
  })
  if (!parsed) return null

  return {
    ...parsed,
    files: record.files as CreateSessionResponse['files'],
    fileID:
      (typeof record.fileID === 'string' ? record.fileID : undefined) ??
      (typeof record.file_id === 'string' ? record.file_id : undefined),
    mainFile:
      (typeof record.mainFile === 'string' ? record.mainFile : undefined) ??
      (typeof record.main_file === 'string' ? record.main_file : undefined),
    pdfpath: typeof record.pdfpath === 'string' ? record.pdfpath : undefined,
    isCompiling: typeof record.isCompiling === 'boolean' ? record.isCompiling : undefined,
    lastCompileAt:
      (typeof record.lastCompileAt === 'string' ? record.lastCompileAt : undefined) ??
      (typeof record.last_compile_at === 'string' ? record.last_compile_at : undefined),
    projectID:
      (typeof record.projectID === 'string' ? record.projectID : undefined) ??
      (typeof record.project_id === 'string' ? record.project_id : undefined),
    workspaceDir:
      (typeof record.workspaceDir === 'string' ? record.workspaceDir : undefined) ??
      (typeof record.workspace_dir === 'string' ? record.workspace_dir : undefined),
  }
}

function normalizeCreateSessionPayload(raw: unknown): CreateSessionResponse | null {
  if (!raw || typeof raw !== 'object') return null
  const payload = raw as Record<string, unknown>
  const parsed = parseCreateSessionResponse(payload)
  if (!parsed) return null

  const mainFile =
    typeof payload.main_file === 'string'
      ? payload.main_file
      : typeof payload.mainFile === 'string'
        ? payload.mainFile
        : parsed.mainFile

  return {
    ...parsed,
    auto_compile: parsed.auto_compile ?? Boolean(payload.auto_compile),
    mainFile,
  }
}

export async function probeCompilerSession(
  sessionId: string,
  signal?: AbortSignal,
): Promise<{ status: number; session: CreateSessionResponse | null }> {
  try {
    const resp = await fetch(`/api/compiler/sessions/${encodeURIComponent(sessionId)}`, {
      headers: { 'Cache-Control': 'no-cache' },
      credentials: 'include',
      signal,
    })
    if (!resp.ok) return { status: resp.status, session: null }

    const data = await resp.json()
    return { status: resp.status, session: normalizeCompilerSessionPayload(data) }
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') throw error
    return { status: 0, session: null }
  }
}

export async function fetchActiveAuthCompilerSession(
  sessionId: string,
  signal?: AbortSignal,
): Promise<CreateSessionResponse | null> {
  const { session } = await probeCompilerSession(sessionId, signal)
  return session
}

export async function createAuthCompilerSessionForProject(
  projectId: string,
  options?: { skipErrorToast?: boolean; signal?: AbortSignal },
): Promise<CreateSessionResponse> {
  const projectSession = await api.createProjectSession(projectId, {
    skipErrorToast: options?.skipErrorToast,
    signal: options?.signal,
  })
  if (!projectSession.success || !projectSession.data?.session_token) {
    throw new Error(projectSession.message || 'Failed to create project session')
  }

  const raw = await api.createCompilerSessionFromToken(projectSession.data.session_token, {
    skipErrorToast: options?.skipErrorToast,
    signal: options?.signal,
  })
  const session = normalizeCreateSessionPayload(raw)
  if (!session) {
    throw new Error('Failed to create compiler session')
  }

  return session
}
